import { IExpense } from "Dashboard/scenes/HumanResources/domain/IExpense";
import ClearIcon from "@mui/icons-material/Clear";
import React, { ChangeEvent, useCallback } from "react";
import { useAppDispatch } from "hooks/hooks";
import {
  deleteExpenseInPayrollForEmployee,
  updateExpense,
  updatePayroll,
  updatePayrollForPrint,
} from "../payroll.slice";
import { IUpdateExpenseInPayrollForEmployeeRequestParams } from "Dashboard/scenes/HumanResources/domain/IUpdateExpenseInPayrollForEmployeeRequestParams";
import { debounce } from "lodash";
import { hrService } from "Dashboard/scenes/HumanResources/services/hr.services";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { HUMAN_RESOURCES_MODULE_NAME } from "Dashboard/scenes/HumanResources/domain/HumanResourcesModule";
import { useSelector } from "react-redux";
import NumberInput from "Dashboard/components/NumberInput";

interface IProps {
  employeeId: number;
  payrollId: number;
  expense: IExpense;
}

export const ExpenseRow = ({ employeeId, payrollId, expense }: IProps) => {
  const dispatch = useAppDispatch();
  const { user } = useSelector(selectStateAuth);

  const delayedExpenseUpdate = useCallback(
    debounce(async (data: IUpdateExpenseInPayrollForEmployeeRequestParams) => {
      try {
        const payroll = await hrService.updateExpenseInPayrollForEmployee(data);
        dispatch(updatePayroll(payroll));
        dispatch(updatePayrollForPrint({
          year: payroll.year,
          month: payroll.month,
          paidExpenses: payroll.expenses.map(expense => ({
            amount: expense.amount,
            note: expense.note
          }))
        }));
      } catch (err) {
        console.log(err);
      }
    }, 1000),
    []
  );

  const onAmountChanged = (value: number) => {
    if (expense) {
      const newExpense = { ...expense, amount: value };
      dispatch(updateExpense({ payrollId, expense: newExpense }));
      delayedExpenseUpdate({ employeeId, payrollId, ...newExpense });
    }
  };
  const onNoteChanged = (event: ChangeEvent<HTMLInputElement>) => {
    if (expense) {
      const newExpense = { ...expense, note: event.target.value };
      dispatch(updateExpense({ payrollId, expense: newExpense }));
      delayedExpenseUpdate({ employeeId, payrollId, ...newExpense });
    }
  };

  const deleteExpense = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      const payroll = await dispatch(
        deleteExpenseInPayrollForEmployee({
          employeeId,
          payrollId,
          expenseId: expense.expenseId,
        })
      ).unwrap();
      if (payroll) {
        dispatch(updatePayroll(payroll));
        dispatch(updatePayrollForPrint({
          year: payroll.year,
          month: payroll.month,
          paidExpenses: payroll.expenses.map(expense => ({
            amount: expense.amount,
            note: expense.note
          }))
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="disabled-space-between">
      <input type="hidden" value={expense.expenseId} />
      <NumberInput
        formatAs="quantity"
        value={`${expense.amount}`}
        onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
          ? (value: string, unmaskedValue: string, parsedValue: number) => {
            if (parsedValue !== expense.amount) {
              onAmountChanged(parsedValue);
            }
          }
          : undefined}
        disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
      />
      <input
        type="text"
        name="note"
        value={expense.note}
        onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? onNoteChanged : undefined}
        disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
      ></input>
      {hasFullAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
        ?
        <button onClick={deleteExpense}>
          <ClearIcon fontSize="small" />
        </button>
        : <></>}
    </div>
  );
};
